import { ChevronDownIcon } from '@heroicons/react/solid'
import {
	RiArrowLeftDoubleLine,
	RiArrowLeftSLine,
	RiArrowRightDoubleLine,
	RiArrowRightSLine
} from 'react-icons/ri'
import Select from 'react-select'
import { twMerge } from 'tailwind-merge'
import { GButton } from '../../g-button'
import { GPaginationProps } from '../types'

const pageSizeOptions = [
	{ value: 10, label: '10' },
	{ value: 20, label: '20' },
	{ value: 50, label: '50' },
	{ value: 100, label: '100' },
	{ value: 250, label: '250' }
]

export const GPagination = ({
	itemsCount = 0,
	pageSize,
	currentPage,
	pageListener,
	onPageChange,
	paginationClassName,
	changePageSize,
	variant
}: GPaginationProps) => {
	const pagesCount = Math.ceil(itemsCount / pageSize)
	const firstItem = (currentPage - 1) * pageSize + 1
	const lastItem =
		firstItem + pageSize - 1 >= itemsCount
			? itemsCount
			: firstItem + pageSize - 1

	if (pagesCount === 1) return null

	// Shared paging helper — preserves the existing skip-based math exactly.
	const goToPage = (target: number) => {
		if (onPageChange) onPageChange(target)
		if (pageListener) pageListener((target - 1) * pageSize)
	}

	if (variant === 'ds') {
		const NavButton = ({
			onClick,
			disabled,
			icon: Icon,
			label
		}: {
			onClick: () => void
			disabled: boolean
			icon: typeof RiArrowLeftSLine
			label: string
		}) => (
			<button
				type="button"
				onClick={onClick}
				disabled={disabled}
				aria-label={label}
				className="flex items-center justify-center h-8 w-8 shrink-0 rounded-[8px] border border-t-checkbox bg-t-smoke shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] disabled:opacity-50 disabled:cursor-not-allowed"
			>
				<Icon className="w-4 h-4 text-t-coupon" />
			</button>
		)

		return (
			<nav
				className={twMerge(
					'flex items-center justify-between gap-3 py-3 bg-t-smoke',
					paginationClassName
				)}
				aria-label="Pagination"
			>
				<div className="flex items-center gap-3">
					{changePageSize && (
						<>
							<span className="font-inter font-medium text-[14px] leading-[20px] text-gray-800">
								Rows per page
							</span>
							<Select
								defaultValue={pageSizeOptions[0]}
								value={pageSizeOptions.find((o) => o.value === pageSize)}
								options={pageSizeOptions}
								menuPlacement="top"
								isSearchable={false}
								isClearable={false}
								onChange={(o) => changePageSize(o?.value || 10)}
								components={{
									IndicatorSeparator: () => null,
									DropdownIndicator: () => (
										<ChevronDownIcon className="h-4 w-4" />
									)
								}}
								styles={{
									control: (base: any) => ({
										...base,
										minHeight: 38,
										backgroundColor: 'var(--pagination-bg)',
										borderColor: 'var(--t-traffic)',
										borderRadius: 8,
										boxShadow: 'none',
										padding: 0
									}),
									menu: (base: any) => ({
										...base,
										backgroundColor: 'var(--bg-popover)',
										boxShadow: 'var(--elevation-popover)',
										zIndex: 20
									}),
									valueContainer: (base: any) => ({
										...base,
										padding: '8px 0 8px 12px',
										fontFamily: 'host-grotesk, sans-serif',
										fontWeight: 500,
										fontSize: 16,
										lineHeight: '22px',
										color: 'var(--t-google-ad)'
									}),
									singleValue: (base: any) => ({
										...base,
										color: 'var(--t-google-ad)'
									}),
									indicatorsContainer: (base: any) => ({
										...base,
										padding: '0 8px 0 4px'
									}),
									option: (styles: any, { isSelected }: any) => ({
										...styles,
										fontSize: 14,
										cursor: 'default',
										backgroundColor: isSelected
											? 'var(--pagination-selected-soft)'
											: 'var(--pagination-bg)',
										color: 'var(--t-google-ad)',
										':hover': {
											backgroundColor: isSelected
												? 'var(--pagination-selected-soft)'
												: 'var(--pagination-hover)'
										}
									})
								}}
							/>
						</>
					)}
				</div>
				<div className="flex items-center gap-3">
					<span className="font-inter font-medium text-[14px] leading-[20px] text-gray-800">
						Page {currentPage} of {pagesCount}
					</span>
					<div className="flex items-center gap-1.5">
						<NavButton
							onClick={() => goToPage(1)}
							disabled={currentPage === 1}
							icon={RiArrowLeftDoubleLine}
							label="First page"
						/>
						<NavButton
							onClick={() => goToPage(currentPage - 1)}
							disabled={currentPage === 1}
							icon={RiArrowLeftSLine}
							label="Previous page"
						/>
						<NavButton
							onClick={() => goToPage(currentPage + 1)}
							disabled={currentPage === pagesCount}
							icon={RiArrowRightSLine}
							label="Next page"
						/>
						<NavButton
							onClick={() => goToPage(pagesCount)}
							disabled={currentPage === pagesCount}
							icon={RiArrowRightDoubleLine}
							label="Last page"
						/>
					</div>
				</div>
			</nav>
		)
	}

	return (
		<nav
			className={twMerge(
				'mt-6 -mb-1 flex items-center justify-between',
				paginationClassName
			)}
			aria-label="Pagination"
		>
			<div className="flex items-center flex-row gap-4">
				{changePageSize && (
					<div className="flex items-center gap-1 text-t-google-ad">
						<span className="text-sm text-t-google-ad">Items per page</span>

						<Select
							defaultValue={pageSizeOptions[0]}
							value={pageSizeOptions.find((o) => o.value === pageSize)}
							options={pageSizeOptions}
							menuPlacement="top"
							isSearchable={false}
							isClearable={false}
							onChange={(o) => changePageSize(o?.value || 10)}
							components={{
								IndicatorSeparator: () => null,
								DropdownIndicator: () => <ChevronDownIcon className="h-3 w-3" />
							}}
							styles={{
								control: (base: any) => ({
									...base,
									minHeight: 24,
									maxHeight: 24,
									backgroundColor: 'var(--pagination-bg)',
									color: 'var(--pagination-text)',
									borderColor: 'var(--t-traffic)',
									padding: 0
								}),
								menu: (base: any) => ({
									...base,
									backgroundColor: 'var(--bg-popover)',
									boxShadow: 'var(--elevation-popover)'
								}),
								valueContainer: (base: any) => ({
									...base,
									padding: '0 2px',
									fontSize: 12
								}),
								indicatorsContainer: (base: any) => ({
									...base,
									padding: '0 2px'
								}),
								dropdownIndicator: (base: any) => ({
									...base,
									padding: '0 2px',
									fontSize: 12
								}),
								clearIndicator: (base: any) => ({
									...base,
									paddingTop: 0,
									paddingBottom: 0
								}),
								option: (styles: any, { isDisabled, isSelected }: any) => {
									return {
										...styles,
										cursor: isDisabled ? 'not-allowed' : 'default',
										fontSize: 12,
										backgroundColor: isSelected
											? 'var(--pagination-selected)'
											: 'var(--pagination-bg)',
										padding: 2,
										color: isSelected ? 'white' : 'var(--pagination-text)',
										display: 'flex',
										':hover': {
											backgroundColor: isSelected
												? 'var(--pagination-selected)'
												: 'var(--pagination-hover)'
										},
										justifyContent: 'center',
										alignItems: 'center'
									}
								}
							}}
						/>
					</div>
				)}
				<p className="text-sm text-t-google-ad">
					Showing <span className="font-medium">({firstItem}</span> to{' '}
					<span className="font-medium">{lastItem})</span> of
					<span className="font-medium"> {itemsCount} </span> results
				</p>
			</div>
			<div className="flex gap-4">
				<GButton
					onClick={() => {
						if (onPageChange) onPageChange(currentPage - 1)
						if (pageListener)
							pageListener(
								(currentPage - 1 === 1 ? 0 : currentPage - 2) * pageSize
							)
					}}
					size="xs"
					disabled={currentPage === 1}
					className="py-1 px-4 text-t-google-ad text-base font-medium rounded-full border-[color:var(--button-outline-border)] disabled:border-gray-200 disabled:bg-transparent disabled:text-gray-400"
					icon={RiArrowLeftSLine}
					iconPlacement="left"
				>
					Previous
				</GButton>
				<GButton
					onClick={() => {
						if (onPageChange) onPageChange(currentPage + 1)
						if (pageListener) pageListener(currentPage * pageSize)
					}}
					size="xs"
					disabled={currentPage === pagesCount}
					className="py-1 px-4 text-t-google-ad text-base font-medium rounded-full border-[color:var(--button-outline-border)] disabled:border-gray-200 disabled:bg-transparent disabled:text-gray-400"
					icon={RiArrowRightSLine}
					iconPlacement="right"
				>
					Next
				</GButton>
			</div>
		</nav>
	)
}
